import React from 'react';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Typography, Checkbox, Button, fade } from '@material-ui/core';
import { Mail, Slack, Icon } from 'react-feather';
import {
  JupiterTheme,
  MockHook,
  LoadingSpinner,
  JupiterOneIcon,
} from '@jupiterone/jupiter-web-toolkit';
import { Category, Channel } from '@jupiterone/notification-types';
import useNotificationsPageLogic, {
  NotificationsPageComponentProps,
} from './useNotificationsPageLogic';
import ChannelHeader from 'navbar/src/jupiterAccountSettings/AccountSettings/Notifications/ChannelHeader';
import ChannelConfiguration from 'navbar/src/jupiterAccountSettings/AccountSettings/Notifications/ChannelConfiguration';

type ChannelHeaderInfoData = {
  label: string;
  icon: Icon;
  tooltip: string;
};
type ChannelHeaderInfo = {
  [key in Channel]: ChannelHeaderInfoData;
};
const CHANNEL_HEADER_INFO: ChannelHeaderInfo = {
  [Channel.EMAIL]: {
    label: 'Email',
    icon: Mail,
    tooltip: 'Sent to your JupiterOne account email',
  },
  [Channel.SLACK]: {
    label: 'Slack',
    icon: Slack,
    tooltip: 'Sent to the configured Slack channel(s)',
  },
  [Channel.IN_APP]: {
    label: 'In-App',
    icon: JupiterOneIcon as Icon,
    tooltip: 'Receive Notifications inside of JupiterOne',
  },
};

const useStyles = makeStyles((theme: JupiterTheme) => {
  return createStyles({
    root: {
      color: theme.palette.common.white,
      margin: theme.spacing(0, 2),
    },
    heading: {
      display: 'flex',
      marginBottom: theme.spacing(4),
    },
    header: {
      flex: 1,
      color: theme.palette.coolGrey[50],
      fontSize: '1.5rem',
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(1),
    },
    channelsHeader: {
      display: 'grid',
      gridTemplateColumns: '3fr 1fr 1fr 1fr',
      marginBottom: theme.spacing(2),
    },
    category: {
      fontSize: '1.5rem',
      fontWeight: theme.typography.fontWeightMedium,
      margin: theme.spacing(2, 0),
    },
    categoryLabel: {
      textTransform: 'capitalize',
      color: theme.palette.primary.main,
    },
    topic: {
      display: 'grid',
      gridTemplateColumns: '3fr 1fr 1fr 1fr',
      margin: theme.spacing(1, 0),
    },
    channelEnabledCheckbox: {
      display: 'flex',
      justifyContent: 'center',
    },
    channelSettingsButtonWrapper: {
      flex: 1,
      textAlign: 'right',
    },
    channelSettingsButton: {
      marginRight: theme.spacing(2),
      color: theme.palette.primary.contrastText,
      borderColor: theme.palette.primary.contrastText,
      '&:hover, &:focus': {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
      },
    },
    actionButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(4),
      paddingRight: theme.spacing(2),
      gap: theme.spacing(1),
    },
    resetButton: {
      color: theme.palette.common.white,
      backgroundColor: fade(theme.palette.common.black, 0.12),
      '&:hover, &:active, &:focus': {
        borderColor: fade(theme.palette.common.black, 0.24),
        backgroundColor: fade(theme.palette.common.black, 0.24),
      },
    },
  });
});

export const NotificationsPageComponent = ({
  loading,
  isSaving,
  hasFormChanged,
  slackIntegrationInstances,
  slackConfiguration,
  isViewingChannelConfiguration,
  categoryTopics,
  handleSave,
  handleReset,
  handleTopicSettingsChange,
  handleSlackConfigurationChange,
  setIsViewingChannelConfiguration,
}: NotificationsPageComponentProps) => {
  const classes = useStyles();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (isViewingChannelConfiguration) {
    return (
      <ChannelConfiguration
        handleBack={() => setIsViewingChannelConfiguration(false)}
        slackIntegrationInstances={slackIntegrationInstances}
        slackConfiguration={slackConfiguration}
        handleSlackConfigurationChange={handleSlackConfigurationChange}
      />
    );
  }
  return (
    <div className={classes.root}>
      <div className={classes.heading}>
        <Typography className={classes.header} variant="h6" component="div">
          Notifications
        </Typography>
        <div className={classes.channelSettingsButtonWrapper}>
          <Button
            className={classes.channelSettingsButton}
            variant="outlined"
            size="small"
            onClick={() => setIsViewingChannelConfiguration(true)}
          >
            Channel Configuration
          </Button>
        </div>
      </div>
      <div className={classes.channelsHeader}>
        <div />
        {Object.values(Channel).map((channel) => (
          <ChannelHeader key={channel} {...CHANNEL_HEADER_INFO[channel]} />
        ))}
      </div>
      {Object.keys(Category).map((category) => (
        <div key={category} className={classes.category}>
          <Typography variant="subtitle1" className={classes.categoryLabel}>
            {category}
          </Typography>
          {categoryTopics[category as Category].map(
            ({
              topic,
              displayName: topicDisplayName,
              settings: topicSettings,
            }) => {
              return (
                <div key={topic} className={classes.topic}>
                  <Typography variant="subtitle2">
                    {topicDisplayName}
                  </Typography>
                  {Object.keys(Channel).map((channel) => (
                    <div
                      key={channel}
                      className={classes.channelEnabledCheckbox}
                    >
                      <Checkbox
                        color="primary"
                        checked={topicSettings[channel as Channel]?.enabled}
                        onChange={({ target: { checked } }) =>
                          handleTopicSettingsChange({
                            category,
                            topic,
                            channel,
                            newEnabledValue: checked,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              );
            }
          )}
        </div>
      ))}
      <div className={classes.actionButtons}>
        <Button
          className={classes.resetButton}
          variant="contained"
          onClick={handleReset}
          disabled={!hasFormChanged}
        >
          Reset
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={!hasFormChanged}
        >
          {isSaving ? <LoadingSpinner size={20} /> : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default MockHook.withHookHoc(
  NotificationsPageComponent,
  useNotificationsPageLogic
);
